import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BedDouble, Check, Clock, MapPin, Maximize, Ticket, Users } from "lucide-react";
import CustomerHeader, { CustomerFooter } from "@/components/customer/CustomerHeader";
import PropertyFooter from "@/components/customer/PropertyFooter";
import { PropertyHeader, setBrand, clearBrand } from "@/components/customer/Brand";
import SearchBar from "@/components/customer/SearchBar";
import { Stars, RatingBadge } from "@/components/customer/HotelCard";
import RatePlanRow from "@/components/customer/RatePlanRow";
import { useShopper } from "@/components/customer/ShopperProvider";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { http, useStay, stayQuery, errMsg, ctxParams } from "@/lib/api";
import { useT, useLang, useLoc } from "@/lib/i18n";
import Img from "@/components/Img";
import RichText from "@/components/RichText";
import ReviewsSection from "@/components/customer/ReviewsSection";
import PhotoGallery from "@/components/customer/PhotoGallery";
import { OtaScores, Attractions, InfoNote, MapBox } from "@/components/customer/PropertyExtras";
import { Images } from "lucide-react";

const useSeo = (p, en) => {
  useEffect(() => {
    if (!p) return undefined;
    const L = (k) => (en && p[`${k}_en`]) || p[k];
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = L("seo_title") || `${p.name} — ${p.city}`;
    meta?.setAttribute("content", L("seo_description") || (L("description") || "").replace(/\*\*/g, "").replace(/\s+/g, " ").slice(0, 160));
    return () => { document.title = prevTitle; meta?.setAttribute("content", prevDesc || ""); };
  }, [p, en]);
};

const planPrice = (pl) => (pl.quote.bookable ? 0 : 1e13) + (pl.quote.avg_per_night ?? 1e12);
const sortRooms = (rooms = []) => rooms
  .map((it) => ({ ...it, plans: [...it.plans].sort((a, b) => planPrice(a) - planPrice(b)) }))
  .sort((a, b) => (a.plans[0] ? planPrice(a.plans[0]) : 1e14) - (b.plans[0] ? planPrice(b.plans[0]) : 1e14));

const RoomDesc = ({ text, id, t }) => {
  const [open, setOpen] = useState(false);
  const long = text.length > 140;
  return (
    <div className="border-t border-slate-100 pt-2">
      <div className={long && !open ? "line-clamp-3" : ""}><RichText text={text} testid={`room-desc-${id}`} className="text-xs text-slate-600" /></div>
      {long && <button type="button" data-testid={`room-desc-toggle-${id}`} onClick={() => setOpen(!open)} className="mt-1 text-xs font-semibold text-[#006CE4] hover:underline">{open ? t("Tutup") : t("Selengkapnya")}</button>}
    </div>
  );
};

const RoomBlock = ({ item, onBook }) => {
  const { room: r, plans, fits } = item;
  const t = useT();
  const loc = useLoc();
  return (
    <div data-testid={`room-row-${r.id}`} className="grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white md:grid-cols-[260px_1fr]">
      <div className="border-b border-slate-100 md:border-b-0 md:border-r">
        <Img src={r.images[0]} sizes="(max-width: 768px) 100vw, 260px" alt={r.name} className="h-44 w-full object-cover" />
        <div className="space-y-2 p-4">
          <h3 className="font-heading text-lg font-bold text-slate-900">{loc(r, "name")}</h3>
          <div className="flex flex-col gap-1 text-sm text-slate-600">
            <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4" /> {loc(r, "bed")}</span>
            <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4" /> {r.size_m2} m²</span>
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {t("Maks {n} tamu", { n: r.max_guests })}</span>
          </div>
          <ul className="grid grid-cols-1 gap-0.5 text-xs text-slate-600">
            {loc(r, "amenities").map((a) => <li key={a} className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-[#00A698]" />{a}</li>)}
          </ul>
          {loc(r, "description") && <RoomDesc text={loc(r, "description")} id={r.id} t={t} />}
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {plans.length === 0 && <p className="p-5 text-sm text-slate-500">{t("Belum ada rate plan untuk kamar ini.")}</p>}
        {plans.map((pl) => <RatePlanRow key={pl.rate_plan.id} room={r} plan={pl} fits={fits} onBook={onBook} />)}
      </div>
    </div>
  );
};

export default function HotelDetail({ propertyId }) {
  const params = useParams();
  const id = propertyId || params.id;
  const stay = useStay();
  const nav = useNavigate();
  const key = stayQuery(stay);
  const { member, country } = useShopper();
  const t = useT();
  const [data, setData] = useState(null);
  const [gallery, setGallery] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState(sessionStorage.getItem("promo_code") || "");
  const [applied, setApplied] = useState(code);

  useEffect(() => {
    http.get(`/public/properties/${id}?${key}`, { params: ctxParams(country, applied) }).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [id, key, country, applied, member?.id]);

  const apply = (e) => { e.preventDefault(); sessionStorage.setItem("promo_code", code.trim()); setApplied(code.trim()); };
  const onBook = (r, rp) => nav(`/checkout?${new URLSearchParams({ ...Object.fromEntries(new URLSearchParams(key)), property: id, room: r.id, plan: rp.id })}`);

  const en = useLang().lang === "en";
  useEffect(() => { if (propertyId) setBrand(propertyId); else clearBrand(); }, [propertyId]);
  useSeo(data?.property, en);
  if (error) return <div className="min-h-screen bg-[#F8FAFC]"><CustomerHeader /><p data-testid="hotel-error" className="mx-auto max-w-3xl p-10 text-center text-red-600">{error}</p></div>;
  const p = data?.property;
  const descText = (en && p?.description_en) || p?.description;
  const standalone = !!propertyId;
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {standalone ? <PropertyHeader p={p} /> : <CustomerHeader />}
      <div className="border-b border-slate-200 bg-[#EAF2FF]">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6"><SearchBar initial={{ ...stay, city: p?.city || stay.city }} compact target={standalone ? window.location.pathname : null} /></div>
      </div>
      {!p ? <div className="mx-auto max-w-7xl p-6"><Skeleton className="h-96 rounded-2xl" /></div> : (
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <div className="relative grid h-[260px] grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-2xl sm:h-[400px]" data-testid="hotel-gallery">
            {p.images.slice(0, 5).map((src, i) => (
              <button type="button" key={src + i} data-testid={`hotel-gallery-photo-${i}`} onClick={() => setGallery(true)} className={`group overflow-hidden ${i === 0 ? "col-span-4 row-span-2 sm:col-span-2" : "hidden sm:block"}`}>
                <Img src={src} size={i === 0 ? "lg" : "md"} eager={i === 0} sizes={i === 0 ? "(max-width: 640px) 100vw, 50vw" : "25vw"} alt={`${p.name} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </button>
            ))}
            <button type="button" data-testid="open-gallery-button" onClick={() => setGallery(true)} className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-lg bg-white/95 px-3 py-2 text-sm font-semibold text-slate-900 shadow hover:bg-white">
              <Images className="h-4 w-4" /> {t("Lihat semua {n} foto", { n: p.images.length + (data.rooms || []).reduce((a, x) => a + (x.room.images?.length || 0), 0) })}</button>
          </div>
          <PhotoGallery open={gallery} onOpenChange={setGallery} property={p} rooms={(data.rooms || []).map((x) => x.room)}
            onReserve={() => setTimeout(() => document.getElementById("rooms-section")?.scrollIntoView({ behavior: "smooth" }), 150)} />
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Stars n={p.stars} />
              <h1 data-testid="hotel-name" className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{p.name}</h1>
              <p className="mt-2 flex items-center gap-1 text-[#5392F9]"><MapPin className="h-4 w-4" /> {p.address}</p>
              {descText && <h2 className="mt-6 font-heading text-xl font-bold text-slate-900">{t("Tentang properti ini")}</h2>}
              <RichText text={descText} testid="hotel-description" className="mt-3" />
            </div>
            <div className="flex flex-col gap-4 self-start rounded-2xl border border-slate-200 bg-white p-5" data-testid="hotel-info-sidebar">
              <RatingBadge rating={p.rating || 0} reviews={p.reviews_count || 0} />
              <OtaScores items={p.ota_scores} />
              <div className="flex gap-6 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Check-in {p.check_in_time}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Check-out {p.check_out_time}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {((en && p.facilities_en?.length) ? p.facilities_en : p.facilities).map((f) => <span key={f} className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700">{f}</span>)}
              </div>
              <MapBox p={p} />
              <Attractions items={p.attractions} />
              <InfoNote note={p.info_note} />
            </div>
          </div>
          <div id="rooms-section" className="mt-10 flex scroll-mt-24 flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-heading text-2xl font-bold text-slate-900">{t("Pilih kamar & harga")}</h2>
            <form onSubmit={apply} className="flex items-center gap-2" data-testid="promo-code-form">
              <div className="relative">
                <Ticket className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input data-testid="promo-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t("Punya kode promo?")} className="w-52 pl-9 font-mono-x uppercase" />
              </div>
              <button data-testid="promo-code-apply" className="rounded-lg border border-[#5392F9] px-4 py-2 text-sm font-bold text-[#5392F9] transition-colors hover:bg-blue-50">{t("Pakai")}</button>
            </form>
          </div>
          {data.promo_code_status && (
            <p data-testid="promo-code-status" className={`mt-2 text-sm font-medium ${data.promo_code_status === "valid" ? "text-[#00A698]" : "text-[#E1231A]"}`}>
              {t(data.promo_code_status === "valid" ? "Kode {c} berhasil dipakai — harga sudah diperbarui." : data.promo_code_status === "not_applicable" ? "Kode {c} tidak berlaku untuk tanggal/kamar ini (atau ada promo lain yang lebih hemat)." : "Kode {c} tidak berlaku di hotel ini.", { c: applied })}
            </p>
          )}
          <div className="mt-4 flex flex-col gap-4">
            {sortRooms(data.rooms).map((item) => <RoomBlock key={item.room.id} item={item} onBook={onBook} />)}
          </div>
          <ReviewsSection pid={p.id} ota={p.ota_reviews} />
        </main>
      )}
      {standalone ? <PropertyFooter p={p} findHref="/find-booking" /> : <CustomerFooter />}
    </div>
  );
}
